/**
 * The `BlockInsertEntity` VIEW of an `ACAD_TABLE`.
 *
 * # Why a view, and why it is not a fabricated entity
 *
 * `ACAD_TABLE` literally IS a block reference in DXF: its first subclass is
 * `AcDbBlockReference`, group 2 names the anonymous `*T<N>` block that carries
 * the table's rendered graphics, and 10/20/30 is the insertion point. Every
 * consumer that needs a table's FOOTPRINT — the WCS bounds walk, the
 * spatial/metadata index — already knows how to resolve a block reference, so
 * they resolve this view instead of each re-deriving "what does a table
 * occupy". Nothing here invents state: unit scale (AcDbTable carries no scale
 * factors), no MINSERT grid, no attributes, and the rotation comes from the
 * horizontal-direction vector through the one shared
 * {@link tableRotationDegrees}.
 *
 * ONE definition, so the bounds walk and the index can never disagree about a
 * table's extent — the reason this is a shared helper rather than a local cast
 * at each site.
 *
 * NOTE this is a READ-ONLY projection for geometry queries. It is NOT a
 * persistence or mutation path: a table is stored and exported as an
 * `ACAD_TABLE`, never as an INSERT.
 */
import { tableRotationDegrees } from "./entityPolylineTableOle";
import type { BlockInsertEntity, TableEntity } from "./model";

export function tableAsBlockReference(entity: TableEntity): BlockInsertEntity {
  return {
    ...entity,
    type: "Block",
    blockName: entity.blockName,
    insertionPoint: entity.insertionPoint,
    scaleX: 1,
    scaleY: 1,
    scaleZ: 1,
    rotation: tableRotationDegrees(entity),
    columnCount: 1,
    rowCount: 1,
    columnSpacing: 0,
    rowSpacing: 0,
    attributes: [],
    annotationScales: [],
    annotativeContexts: [],
    customProperties: {},
  } as unknown as BlockInsertEntity;
}
